/**
 * Value types and on-disk format for the encrypted-local-backup file (web).
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BackupBundle.swift` — iOS is treated as
 * canonical for cross-platform interop; the byte layout of the canonical AAD
 * region is byte-for-byte identical so a future cross-platform restore is
 * possible without re-sealing.
 *
 * THREAT MODEL
 *   - Backup file at rest is untrusted: an attacker may flip bytes anywhere
 *     (header or ciphertext). Defense: AES-GCM with the canonical-encoded
 *     header as AAD — any tamper surfaces as a defined `BackupErrorKind`
 *     ("authFailed" or "unsupportedMagic" / "unsupportedKdf").
 *   - Server intermediary tampers with the header so a future restore
 *     decrypts under attacker-chosen KDF params. Defense: the header is
 *     bound as AES-GCM AAD; any byte flip surfaces as `authFailed`.
 *   - Future-format schism: a decade-old backup, the current app has
 *     rotated KDF parameters. Defense: the on-disk `kdfAlgorithm` string
 *     is a discriminator — a future Argon2id case can coexist with already-
 *     generated `pbkdf2-sha256-600k` files.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs. Pure types +
 *   pure byte encoding only.
 */

/**
 * Magic prefix identifying every kaether backup file. Versioned so a future
 * format change (e.g. `kaether-backup-v2`) can coexist with already-written
 * files; the parser dispatches on the version after sniffing this magic.
 */
export const BACKUP_MAGIC = "kaether-backup-v1";

/**
 * KDF algorithm slot for the encrypted-local-backup file. A single value
 * today; the discriminator exists so a future Argon2id migration (via WASM)
 * can coexist with already-generated `pbkdf2-sha256-600k` files.
 *
 * The web baseline is PBKDF2-SHA256 with 600 000 iterations because that is
 * what `crypto.subtle` ships natively. iOS uses a different KDF (iterated
 * HKDF-SHA256) for the same reason — the per-platform crypto-stdlib
 * dictates the baseline. Both platforms tag the algorithm on disk so the
 * restore path can dispatch correctly.
 */
export type BackupKdfAlgorithm = "pbkdf2-sha256-600k";

/** Allow-list of every algorithm string this build can decode. */
export const KNOWN_KDF_ALGORITHMS: readonly BackupKdfAlgorithm[] = [
  "pbkdf2-sha256-600k",
];

/** 32 bytes — salt length for every KDF case. Mirrors iOS. */
export const BACKUP_SALT_BYTES = 32;

/** 12 bytes — AES-GCM nonce length. Standard for GCM. */
export const BACKUP_NONCE_BYTES = 12;

/** 16 bytes — AES-GCM auth-tag length. Standard. */
export const BACKUP_TAG_BYTES = 16;

/**
 * Parsed header. Bound as AES-GCM AAD via `canonicalHeaderBytes(...)`, so any
 * byte flip in the salt, nonce, algorithm, or createdAt field flips the AAD
 * and the open() path surfaces `authFailed`.
 */
export interface BackupHeader {
  /** Magic prefix. Always equals `BACKUP_MAGIC` for a valid v1 file. */
  readonly magic: string;
  /** KDF used to derive the AES-GCM key from the passphrase. */
  readonly kdfAlgorithm: BackupKdfAlgorithm;
  /** 32 random bytes mixed into the passphrase derivation. Fresh per seal. */
  readonly kdfSalt: Uint8Array;
  /** 12-byte AES-GCM nonce. Fresh per seal — never reused under the same key. */
  readonly cipherNonce: Uint8Array;
  /** Unix seconds at seal time. Included in canonical AAD bytes. */
  readonly createdAt: number;
}

/**
 * Discriminated parse-failure kinds. The UI maps each to distinct copy so
 * the user can tell "not a backup file" from "wrong passphrase" from
 * "future-version-we-cannot-read". Mirrors iOS `BackupError` cases.
 */
export type BackupErrorKind =
  | "malformedHeader"
  | "unsupportedMagic"
  | "unsupportedKdfAlgorithm"
  | "invalidSaltLength"
  | "invalidNonceLength"
  | "authFailed"
  | "payloadOverLimit";

/** Error type thrown by every parse / seal / open helper in this module. */
export class BackupError extends Error {
  readonly kind: BackupErrorKind;
  readonly detail?: string | number;

  constructor(kind: BackupErrorKind, detail?: string | number) {
    super(`BackupError: ${kind}${detail != null ? ` (${detail})` : ""}`);
    this.name = "BackupError";
    this.kind = kind;
    this.detail = detail;
  }
}

/**
 * Deterministic byte-string used as AES-GCM AAD. Layout (byte-for-byte
 * compatible with the iOS canonical encoder):
 *
 *   magic || 0x1F || kdfAlgorithm || 0x1F
 *         || kdfSalt || 0x1F
 *         || cipherNonce || 0x1F
 *         || createdAt(8 bytes little-endian)
 *
 * The 0x1F (ASCII unit-separator) byte is rare in JSON / UTF-8 strings so
 * it doubles as a visible boundary in hex dumps. The little-endian timestamp
 * is fixed-width so the AAD length is stable and a parser cannot ambiguate
 * field boundaries.
 *
 * Pure function — no I/O, safe in Service Worker.
 */
export function canonicalHeaderBytes(h: BackupHeader): Uint8Array {
  const enc = new TextEncoder();
  const magicBytes = enc.encode(h.magic);
  const algoBytes = enc.encode(h.kdfAlgorithm);
  const total =
    magicBytes.length + 1 +
    algoBytes.length + 1 +
    h.kdfSalt.length + 1 +
    h.cipherNonce.length + 1 +
    8;
  const out = new Uint8Array(total);
  let o = 0;
  out.set(magicBytes, o); o += magicBytes.length;
  out[o++] = 0x1F;
  out.set(algoBytes, o); o += algoBytes.length;
  out[o++] = 0x1F;
  out.set(h.kdfSalt, o); o += h.kdfSalt.length;
  out[o++] = 0x1F;
  out.set(h.cipherNonce, o); o += h.cipherNonce.length;
  out[o++] = 0x1F;
  // Little-endian 8-byte signed integer (BigInt to handle the full range).
  const view = new DataView(out.buffer, out.byteOffset + o, 8);
  view.setBigInt64(0, BigInt(h.createdAt), /* littleEndian = */ true);
  return out;
}

/**
 * On-disk shape of a sealed backup. The file wraps this as
 *   UTF-8(magic) || JSON(BackupBundleJSON)
 * with byte arrays base64-encoded inside the JSON.
 */
export interface BackupBundleJSON {
  readonly header: {
    readonly magic: string;
    readonly kdfAlgorithm: string;
    readonly kdfSalt: string;     // base64
    readonly cipherNonce: string; // base64
    readonly createdAt: number;
  };
  readonly ciphertext: string;    // base64
  readonly authTag: string;       // base64
}

export interface ParsedBundle {
  readonly header: BackupHeader;
  readonly ciphertext: Uint8Array;
  readonly authTag: Uint8Array;
}

// ---------------------------------------------------------------------------
// File-bytes encode / parse
// ---------------------------------------------------------------------------

/**
 * Encode a header + ciphertext + tag into the on-disk file format. Used
 * by the seal path and by tests that hand-build malformed files.
 *
 * Layout: UTF-8(magic) || JSON(sortedKeys). The magic is duplicated inside
 * the JSON header so a parser that strips the prefix can still verify it
 * inside the JSON. AES-GCM AAD covers the JSON header anyway.
 */
export function encodeFileBytes(
  header: BackupHeader,
  ciphertext: Uint8Array,
  authTag: Uint8Array,
): Uint8Array {
  const bundle: BackupBundleJSON = {
    header: {
      magic: header.magic,
      kdfAlgorithm: header.kdfAlgorithm,
      kdfSalt: base64Encode(header.kdfSalt),
      cipherNonce: base64Encode(header.cipherNonce),
      createdAt: header.createdAt,
    },
    ciphertext: base64Encode(ciphertext),
    authTag: base64Encode(authTag),
  };
  // Stable sorted-keys serialiser so the file bytes are reproducible across
  // platforms. We sort the top-level keys and the header sub-object keys
  // manually because JSON.stringify in JS does NOT guarantee key order.
  const json = stableStringify(bundle);
  const enc = new TextEncoder();
  const magicBytes = enc.encode(header.magic);
  const jsonBytes = enc.encode(json);
  const out = new Uint8Array(magicBytes.length + jsonBytes.length);
  out.set(magicBytes, 0);
  out.set(jsonBytes, magicBytes.length);
  return out;
}

/**
 * Parse a file-bytes blob into a typed bundle. Rejects:
 *   - input shorter than the magic prefix
 *   - magic prefix mismatch
 *   - JSON decode failure
 *   - unknown kdfAlgorithm string
 *
 * Does NOT enforce salt/nonce length constraints — those are enforced by
 * the caller (typically `BackupCipher.open`) so the error surface stays
 * specific (`invalidSaltLength` vs `invalidNonceLength`).
 */
export function parseFileBytes(fileBytes: Uint8Array): ParsedBundle {
  const enc = new TextEncoder();
  const magicBytes = enc.encode(BACKUP_MAGIC);
  if (fileBytes.length < magicBytes.length) {
    throw new BackupError("unsupportedMagic");
  }
  for (let i = 0; i < magicBytes.length; i++) {
    if (fileBytes[i] !== magicBytes[i]) {
      throw new BackupError("unsupportedMagic");
    }
  }
  const jsonBytes = fileBytes.subarray(magicBytes.length);
  const dec = new TextDecoder("utf-8", { fatal: true });
  let jsonText: string;
  try {
    jsonText = dec.decode(jsonBytes);
  } catch (e) {
    throw new BackupError("malformedHeader", "non-utf8 body");
  }
  let raw: unknown;
  try {
    raw = JSON.parse(jsonText);
  } catch (e) {
    throw new BackupError("malformedHeader", "json decode");
  }
  if (!isRawBundle(raw)) {
    throw new BackupError("malformedHeader", "shape");
  }
  if (!(KNOWN_KDF_ALGORITHMS as readonly string[]).includes(raw.header.kdfAlgorithm)) {
    throw new BackupError("unsupportedKdfAlgorithm", raw.header.kdfAlgorithm);
  }
  const header: BackupHeader = {
    magic: raw.header.magic,
    kdfAlgorithm: raw.header.kdfAlgorithm as BackupKdfAlgorithm,
    kdfSalt: base64Decode(raw.header.kdfSalt),
    cipherNonce: base64Decode(raw.header.cipherNonce),
    createdAt: raw.header.createdAt,
  };
  if (header.magic !== BACKUP_MAGIC) {
    throw new BackupError("unsupportedMagic");
  }
  return {
    header,
    ciphertext: base64Decode(raw.ciphertext),
    authTag: base64Decode(raw.authTag),
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isRawBundle(v: unknown): v is BackupBundleJSON {
  if (typeof v !== "object" || v === null) return false;
  const r = v as Record<string, unknown>;
  if (typeof r.ciphertext !== "string") return false;
  if (typeof r.authTag !== "string") return false;
  if (typeof r.header !== "object" || r.header === null) return false;
  const h = r.header as Record<string, unknown>;
  return (
    typeof h.magic === "string" &&
    typeof h.kdfAlgorithm === "string" &&
    typeof h.kdfSalt === "string" &&
    typeof h.cipherNonce === "string" &&
    typeof h.createdAt === "number"
  );
}

/**
 * Stable serialiser. Top-level keys are emitted in fixed order
 * (`authTag, ciphertext, header`), and the header sub-object keys in
 * fixed order (`cipherNonce, createdAt, kdfAlgorithm, kdfSalt, magic`).
 * The order matches iOS's `JSONEncoder.outputFormatting = [.sortedKeys]`
 * so file bytes can be diffed across platforms.
 */
function stableStringify(b: BackupBundleJSON): string {
  const headerStr = JSON.stringify({
    cipherNonce: b.header.cipherNonce,
    createdAt: b.header.createdAt,
    kdfAlgorithm: b.header.kdfAlgorithm,
    kdfSalt: b.header.kdfSalt,
    magic: b.header.magic,
  });
  return `{"authTag":${JSON.stringify(b.authTag)},"ciphertext":${JSON.stringify(b.ciphertext)},"header":${headerStr}}`;
}

/** Base64 encoder using btoa with a binary-string intermediate. */
function base64Encode(bytes: Uint8Array): string {
  let s = "";
  // Chunked to avoid String.fromCharCode arg-count limits on large inputs.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, Math.min(i + CHUNK, bytes.length))),
    );
  }
  // `btoa` is available in both window and Service Worker globals.
  return btoa(s);
}

/** Base64 decoder using atob with a binary-string intermediate. */
function base64Decode(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}
